import { z } from 'zod';
import { pennylaneInvoiceStatusSchema } from '../status/pennylane.js';
import { flowModeSchema, runStatusSchema, runTriggerSchema } from '../status/run.js';
import { dateRangeQuerySchema, decimalStringSchema, handledByV1Schema, isoDateTimeSchema, isoDaySchema, objectIdSchema, paginationQuerySchema, schedulePresetSchema, sortQuerySchema, } from './common.js';
/** Compteurs d'un run Pennylane (`03` §2, `pennylaneRuns.stats`). */
export const pennylaneRunStatsSchema = z.object({
    listed: z.number().int().min(0),
    sent: z.number().int().min(0),
    ignored: z.number().int().min(0),
    errors: z.number().int().min(0),
});
export const pennylaneRunListItemSchema = z.object({
    id: objectIdSchema,
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    trigger: runTriggerSchema,
    triggeredBy: objectIdSchema.nullable(),
    simulation: z.boolean(),
    status: runStatusSchema,
    stats: pennylaneRunStatsSchema,
    dates: z.array(isoDaySchema),
    error: z.string().nullable(),
});
export const pennylaneInvoiceLineStatsSchema = z.object({
    api: z.number().int().min(0),
    emitted: z.number().int().min(0),
    skipped: z.number().int().min(0),
});
export const pennylaneInvoiceListItemSchema = z.object({
    id: objectIdSchema,
    invoiceId: z.string(),
    invoiceNumber: z.string(),
    date: isoDaySchema,
    customerName: z.string().nullable(),
    amount: decimalStringSchema.nullable(),
    status: pennylaneInvoiceStatusSchema,
    simulated: z.boolean().optional(),
    handledByV1: handledByV1Schema.nullable().optional(),
    reason: z.string().nullable(),
    fileId: objectIdSchema.nullable(),
    fileName: z.string().nullable(),
    lineStats: pennylaneInvoiceLineStatsSchema.nullable(),
    attempts: z.number().int().min(0),
    lastError: z.string().nullable(),
    sentAt: isoDateTimeSchema.nullable(),
    forcedBy: objectIdSchema.nullable(),
});
/** Entrée d'historique d'une facture (`02` §3, page de détail). */
export const pennylaneInvoiceTimelineEntrySchema = z.object({
    at: isoDateTimeSchema,
    code: z.string(),
    message: z.string(),
    userName: z.string().nullable(),
});
/** Dernière « Mise à jour depuis Pennylane » : ce que donnerait un traitement maintenant, sans envoi. */
export const pennylaneInvoicePreviewSchema = z.object({
    calculatedAt: isoDateTimeSchema,
    decision: z.enum(['ready', 'ignored', 'error']),
    reason: z.string().nullable(),
    csvContent: z.string().nullable(),
});
/** `GET /api/pennylane/invoices/:id` — la fiche facture ouverte depuis Commandes. */
export const pennylaneInvoiceDetailSchema = z.object({
    ...pennylaneInvoiceListItemSchema.shape,
    timeline: z.array(pennylaneInvoiceTimelineEntrySchema),
    preview: pennylaneInvoicePreviewSchema.nullable(),
});
export const pennylaneInvoiceListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    status: z.array(pennylaneInvoiceStatusSchema).optional(),
    search: z.string().min(1).optional(),
});
export const pennylaneRunListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    status: z.array(runStatusSchema).optional(),
});
/** Lancement manuel par date ou par facture (`01` §6.4). */
export const pennylaneManualRunBodySchema = z.object({
    date: isoDaySchema.optional(),
    invoiceIds: z.array(z.string().min(1)).optional(),
    invoiceNumber: z.string().min(1).optional(),
    /** Confirmation explicite pour renvoyer une facture déjà envoyée. */
    confirmResend: z.boolean().default(false),
});
export const pennylaneSettingsSummarySchema = z.object({
    enabled: z.boolean(),
    mode: flowModeSchema,
    apiUrl: z.string(),
    apiKeyConfigured: z.boolean(),
    schedule: z.string(),
    scheduleDescription: z.string(),
    catchupDays: z.number().int().min(1),
    importFlag: z.object({ name: z.string(), value: z.string() }),
    /** Transporteur des CSV de facture ; `null` = bloc transport vide (v1). */
    defaultCarrier: z.object({ code: z.string(), label: z.string() }).nullable(),
    nextRuns: z.array(isoDateTimeSchema),
    runInProgress: z.boolean(),
});
/**
 * Mise à jour des réglages Pennylane (`02` §7 et §12.4) : planification en
 * préréglage ou cron (`01` §6.1), clé API en écriture seule (`03` §1 — jamais
 * relue en clair).
 */
export const pennylaneSettingsUpdateBodySchema = z
    .object({
    enabled: z.boolean(),
    mode: flowModeSchema,
    apiUrl: z.string().min(1),
    /** Absent = ne pas modifier la clé stockée. */
    apiKey: z.string().min(1).optional(),
    schedule: z.string().min(1).optional(),
    preset: schedulePresetSchema.optional(),
    catchupDays: z.number().int().min(1),
    importFlag: z.object({ name: z.string().min(1), value: z.string().min(1) }),
    defaultCarrier: z.object({ code: z.string().min(1), label: z.string().min(1) }).nullable(),
})
    .refine((body) => body.schedule !== undefined || body.preset !== undefined, {
    message: 'schedule ou preset requis',
    path: ['schedule'],
});
/** `POST /api/pennylane/run` — « Lancer maintenant » / « Lancer en simulation » (`01` §6.1, `02` §7). */
export const pennylaneRunStartBodySchema = z.object({
    simulation: z.boolean().default(false),
});
/** Résultat du lancement manuel, reflet de `flows/pennylane` `PennylaneRunSummary`. */
export const pennylaneRunResultSchema = z.object({
    runId: objectIdSchema.nullable(),
    status: z.enum(['done', 'failed', 'skipped']),
    dates: z.array(isoDaySchema),
    simulation: z.boolean(),
    stats: pennylaneRunStatsSchema,
    enqueued: z.array(z.string()),
    processedInline: z.array(z.string()),
    error: z.string().nullable(),
});
/** Détail d'un run (`02` §7) : le run plus les factures dont la date tombe dans sa fenêtre. */
export const pennylaneRunDetailSchema = z.object({
    ...pennylaneRunListItemSchema.shape,
    invoices: z.array(pennylaneInvoiceListItemSchema),
});
/** `GET /api/pennylane/manual/day` — liste des factures d'un jour avant sélection (`01` §6.4). */
export const pennylaneManualDayQuerySchema = z.object({
    date: isoDaySchema,
});
/** « Lancer manuellement » par date (`01` §6.4, `02` §7) : sélection dans la liste du jour. */
export const pennylaneManualByDateBodySchema = z.object({
    date: isoDaySchema,
    invoiceIds: z.array(z.string().min(1)).min(1, 'Sélectionnez au moins une facture'),
    confirmResend: z.boolean().default(false),
});
export const pennylaneManualSendResultSchema = z.object({
    enqueued: z.array(z.string()),
});
/** « Lancer manuellement » par numéro ou id de facture (`01` §6.4, `02` §7). */
export const pennylaneManualByIdBodySchema = z.object({
    idOrNumber: z.string().min(1),
    confirmResend: z.boolean().default(false),
});
export const pennylaneManualByIdResultSchema = z.object({
    /** Identifiant local (`_id`), pour ouvrir directement `/commandes/factures/:id`. */
    id: objectIdSchema,
    invoiceId: z.string(),
    invoiceNumber: z.string(),
});
/** « Renvoyer » une facture déjà connue (`02` §7) : confirmation explicite si déjà envoyée. */
export const pennylaneResendBodySchema = z.object({
    confirmResend: z.boolean().default(false),
});
/** « Ignorer » une facture (`02` §7) : motif obligatoire, comme les lignes de fulfillment. */
export const pennylaneIgnoreBodySchema = z.object({
    reason: z.string().min(1, 'Motif obligatoire'),
});
//# sourceMappingURL=pennylane.js.map